// Date helpers for the date picker: MM/DD/YYYY in and out, and calendar steps.

const pad = (n: number) => String(n).padStart(2, '0')

/** Month first (10/19/2026), the U.S. convention, or ISO 8601 (2026-10-19). */
export type DateFormat = 'mdy' | 'iso'
export const dateHints: Record<DateFormat, string> = { mdy: 'mm/dd/yyyy', iso: 'yyyy-mm-dd' }

/** Where a field's format hint shows: above the field, as its placeholder, or both. */
export type HintPlacement = 'above' | 'placeholder' | 'both'

export const formatDate = (date: Date, format: DateFormat = 'mdy') =>
  format === 'iso'
    ? `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    : `${pad(date.getMonth() + 1)}/${pad(date.getDate())}/${date.getFullYear()}`

/** Reads a date in the given format, returning null for anything that is not a real date. */
export function parseDate(text: string, format: DateFormat = 'mdy'): Date | null {
  const match = format === 'iso' ? /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(text.trim()) : /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(text.trim())
  if (!match) return null
  const [month, day, year] =
    format === 'iso' ? [Number(match[2]), Number(match[3]), Number(match[1])] : [Number(match[1]), Number(match[2]), Number(match[3])]
  const date = new Date(year, month - 1, day)
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day ? date : null
}

export const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString()
export const addDays = (date: Date, days: number) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + days)
export const addMonths = (date: Date, count: number) => {
  const target = new Date(date.getFullYear(), date.getMonth() + count, 1)
  const last = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate()
  return new Date(target.getFullYear(), target.getMonth(), Math.min(date.getDate(), last))
}

/** Times of day from start to end, in steps of `step` minutes, written as "9:30 am" or, on a 24-hour clock, "09:30". */
export function timesOfDay(start = 0, end = 24 * 60 - 1, step = 30, clock: 12 | 24 = 12) {
  if (!(step > 0)) throw new RangeError(`timesOfDay needs a step above zero, not ${step}`)
  const times: string[] = []
  for (let minutes = start; minutes <= end; minutes += step) {
    const hour = Math.floor(minutes / 60)
    times.push(clock === 24 ? `${pad(hour)}:${pad(minutes % 60)}` : `${hour % 12 || 12}:${pad(minutes % 60)} ${hour < 12 ? 'am' : 'pm'}`)
  }
  return times
}

/** The United States' time zones: IANA names, the zone's name and a city in it. */
export const usTimeZones = [
  { zone: 'America/New_York', name: 'Eastern Time', city: 'New York' },
  { zone: 'America/Chicago', name: 'Central Time', city: 'Chicago' },
  { zone: 'America/Denver', name: 'Mountain Time', city: 'Denver' },
  { zone: 'America/Phoenix', name: 'Mountain Time', city: 'Phoenix' },
  { zone: 'America/Los_Angeles', name: 'Pacific Time', city: 'Los Angeles' },
  { zone: 'America/Anchorage', name: 'Alaska Time', city: 'Anchorage' },
  { zone: 'Pacific/Honolulu', name: 'Hawaii-Aleutian Time', city: 'Honolulu' },
  { zone: 'America/Puerto_Rico', name: 'Atlantic Time', city: 'San Juan' },
  { zone: 'Pacific/Guam', name: 'Chamorro Time', city: 'Hagåtña' },
  { zone: 'Pacific/Pago_Pago', name: 'Samoa Time', city: 'Pago Pago' },
]

/** A zone's offset from UTC on a given date, as "UTC-04:00", so daylight saving time is reflected. */
export function utcOffset(zone: string, on = new Date()) {
  const part = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'longOffset' })
    .formatToParts(on)
    .find((item) => item.type === 'timeZoneName')?.value
  const offset = part?.replace('GMT', '') || '+00:00'
  return `UTC${offset}`
}

/** "(UTC-04:00) Eastern Time - New York" */
export const zoneLabel = ({ zone, name, city }: (typeof usTimeZones)[number], on?: Date) => `(${utcOffset(zone, on)}) ${name} - ${city}`
